package co.maytes.standupbot.dashboard.features.releases

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.Disclosure
import co.maytes.standupbot.dashboard.components.motion.rotatesWhenOpen
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.LinkButton
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideChevronDown
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.ship.ShipEventDto
import co.maytes.standupbot.shared.format.ReleaseTimeFormat
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private const val DISCLOSURE_SETTLE_MS = 40L
private const val DASH = "–"

internal fun Modifier.singleLine(): Modifier =
    styleModifier {
        property("min-width", "0")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }

@Composable
internal fun LazyDisclosure(
    open: Boolean,
    content: @Composable () -> Unit,
) {
    var rendered by remember { mutableStateOf(open) }
    LaunchedEffect(open) {
        if (open) {
            rendered = true
        } else {
            delay(Motion.DISCLOSURE_MS + DISCLOSURE_SETTLE_MS)
            rendered = false
        }
    }
    if (open || rendered) Disclosure(open = open, animateInitial = true) { content() }
}

@Composable
private fun KindMark(
    release: Boolean,
    modifier: Modifier = Modifier,
) {
    val tone: CSSColorValue = if (release) Palette.accentText else Palette.textTertiary
    Box(
        modifier =
            modifier.color(tone).styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
            },
    ) {
        if (release) ReleaseTagIcon() else MergedPullRequestIcon()
    }
}

@Composable
fun LatestReleasesStrip(
    releases: List<ShipEventDto>,
    now: Instant,
    zone: TimeZone,
) {
    if (releases.isEmpty()) return
    Div(attrs = Modifier.fillMaxWidth().margin(bottom = Spacing.md).toAttrs { attr("aria-label", "Latest releases") }) {
        SpanText(
            text = "Latest releases",
            modifier =
                Modifier
                    .margin(bottom = Spacing.xs)
                    .fontSize(Typography.caption.size)
                    .fontWeight(Typography.caption.weight)
                    .color(Palette.textTertiary)
                    .styleModifier { property("display", "block") },
        )
        Div(attrs = Modifier.classNames(FeedClasses.CARDS).toAttrs()) {
            releases.forEach { ReleaseMilestoneCard(it, now, zone) }
        }
    }
}

@Composable
private fun ReleaseMilestoneCard(
    event: ShipEventDto,
    now: Instant,
    zone: TimeZone,
) {
    A(
        href = event.url,
        attrs =
            Modifier.classNames(FeedClasses.CARD).toAttrs {
                attr("target", "_blank")
                attr("rel", "noopener noreferrer")
                attr("title", event.title)
                attr("aria-label", "Release ${event.ref} in ${event.repo}, open on GitHub")
            },
    ) {
        Row(horizontalArrangement = Arrangement.spacedBy(Spacing.xs), verticalAlignment = Alignment.CenterVertically) {
            KindMark(release = true)
            SpanText(
                text = event.repo,
                modifier =
                    Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textSecondary)
                        .singleLine(),
            )
        }
        SpanText(
            text = event.ref,
            modifier =
                Modifier
                    .fontFamily(*Typography.MONO_STACK)
                    .fontSize(Typography.h4.size)
                    .fontWeight(Typography.h4.weight)
                    .color(Palette.textPrimary)
                    .singleLine(),
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                text = ReleaseFeedGroups.relative(now, event.occurredAt, zone),
                modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textTertiary),
            )
            Box(modifier = Modifier.color(Palette.textTertiary)) { ExternalLinkIcon() }
        }
    }
}

@Composable
fun ShipEventRow(
    event: ShipEventDto,
    zone: TimeZone,
    now: Instant,
    expanded: Boolean,
    onToggle: () -> Unit,
) {
    val release = event.isRelease
    val detailId = "sb-rel-detail-${event.id}"
    Div(attrs = Modifier.classNames(FeedClasses.ITEM).toAttrs()) {
        Button(
            attrs =
                Modifier.classNames(FeedClasses.ROW).onClick { onToggle() }.toAttrs {
                    attr("type", "button")
                    attr("aria-expanded", expanded.toString())
                    attr("aria-controls", detailId)
                },
        ) {
            KindMark(release = release, modifier = Modifier.classNames(FeedClasses.ICON))
            SpanText(
                text = event.title,
                modifier =
                    Modifier
                        .classNames(FeedClasses.TITLE)
                        .fontSize(Typography.body.size)
                        .fontWeight(if (release) Typography.h4.weight else Typography.label.weight)
                        .color(Palette.textPrimary)
                        .singleLine(),
            )
            Div(attrs = Modifier.classNames(FeedClasses.META).toAttrs()) {
                RefCell(event)
                SpanText(
                    text = event.repo,
                    modifier =
                        Modifier
                            .fontSize(Typography.bodySmall.size)
                            .color(Palette.textSecondary)
                            .singleLine(),
                )
                AuthorCell(event)
                SpanText(
                    text = ReleaseFeedGroups.relative(now, event.occurredAt, zone),
                    modifier =
                        Modifier
                            .classNames(FeedClasses.TIME)
                            .fontSize(Typography.bodySmall.size)
                            .color(Palette.textTertiary)
                            .attrsModifier { attr("title", ReleaseTimeFormat.absolute(event.occurredAt, zone)) },
                )
            }
            Box(modifier = Modifier.classNames(FeedClasses.CHEVRON).color(Palette.textTertiary).rotatesWhenOpen(expanded)) {
                LucideChevronDown(size = 16)
            }
        }
        Div(attrs = Modifier.attrsModifier { attr("id", detailId) }.toAttrs()) {
            LazyDisclosure(open = expanded) { ShipEventDetails(event, zone) }
        }
    }
}

@Composable
private fun RefCell(event: ShipEventDto) {
    if (event.isRelease) {
        Box(modifier = Modifier.singleLine()) { Badge(label = event.ref, tone = BadgeTone.Accent) }
    } else {
        SpanText(
            text = "#${event.ref}",
            modifier =
                Modifier
                    .fontFamily(*Typography.MONO_STACK)
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary)
                    .singleLine(),
        )
    }
}

@Composable
private fun AuthorCell(event: ShipEventDto) {
    Row(
        modifier = Modifier.classNames(FeedClasses.AUTHOR).gap(Spacing.xs).singleLine(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (event.authorLogin.isEmpty()) {
            SpanText(text = DASH, modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textTertiary))
        } else {
            SpanText(
                text = event.authorLogin,
                modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textSecondary).singleLine(),
            )
            if (event.authorIsBot) Badge(label = "bot", tone = BadgeTone.Neutral)
        }
    }
}

@Composable
private fun ShipEventDetails(
    event: ShipEventDto,
    zone: TimeZone,
) {
    val verb = if (event.isRelease) "Released" else "Merged"
    Div(
        attrs =
            Modifier
                .classNames(FeedClasses.DETAIL)
                .toAttrs { attr("role", "region") },
    ) {
        Div(attrs = Modifier.fillMaxWidth().styleModifier { property("display", "grid") }.gap(Spacing.sm).toAttrs()) {
            if (event.body.isBlank()) {
                SpanText(
                    text = "No description.",
                    modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textTertiary),
                )
            } else {
                ReleaseMarkdownView(event.body.trim())
                if (event.bodyTruncated) {
                    SpanText(
                        text = "The description is shortened here. Open it on GitHub to read the rest.",
                        modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textTertiary),
                    )
                }
            }
            if (event.labels.isNotEmpty()) {
                Row(
                    modifier = Modifier.fillMaxWidth().gap(Spacing.xs).styleModifier { property("flex-wrap", "wrap") },
                    verticalAlignment = Alignment.CenterVertically,
                ) { event.labels.forEach { Badge(label = it, tone = BadgeTone.Neutral) } }
            }
            Row(
                modifier = Modifier.fillMaxWidth().gap(Spacing.sm).styleModifier { property("flex-wrap", "wrap") },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                LinkButton(
                    label = "Open on GitHub",
                    href = event.url,
                    variant = ButtonVariant.Secondary,
                    size = ButtonSize.Sm,
                    external = true,
                    trailingIcon = { ExternalLinkIcon() },
                )
                SpanText(
                    text = "$verb ${ReleaseTimeFormat.absolute(event.occurredAt, zone)} ${zoneLabel(event.occurredAt, zone)}",
                    modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textTertiary),
                )
                SpanText(
                    text = "Needs GitHub access",
                    modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textTertiary),
                )
            }
        }
    }
}

@Composable
fun DayHeading(
    label: String,
    count: Int,
) {
    Div(
        attrs =
            Modifier.classNames(FeedClasses.DAY).toAttrs {
                attr("role", "heading")
                attr("aria-level", "3")
            },
    ) {
        SpanText(
            text = label,
            modifier =
                Modifier
                    .fontSize(Typography.label.size)
                    .fontWeight(Typography.h4.weight)
                    .color(Palette.textPrimary),
        )
        SpanText(
            text = count.toString(),
            modifier =
                Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary)
                    .styleModifier { property("font-variant-numeric", "tabular-nums") },
        )
    }
}
